// Utilitaires partagés pour les formulaires d'inscription.
// - Bornes de date pour l'input date de naissance
// - Formatage / validation du téléphone suisse
// - Auto-complétion NPA → Localité (Suisse Romande + grandes villes)

const currentIso = () => new Date().toISOString().slice(0, 10);
export const BIRTH_DATE_MIN = "1930-01-01";
export const BIRTH_DATE_MAX = currentIso();

/**
 * Formate un numéro de téléphone suisse au fur et à mesure de la frappe.
 * Accepte l'entrée libre — normalise "+41 79 950 40 31" ou "079 950 40 31".
 */
export function formatSwissPhone(raw) {
  if (!raw) return "";
  // Nettoie tout sauf chiffres et le '+' initial
  let cleaned = raw.replace(/[^\d+]/g, "");
  if (cleaned.startsWith("+")) {
    cleaned = "+" + cleaned.slice(1).replace(/\D/g, "");
  }

  // Cas 1 : format international +41…
  if (cleaned.startsWith("+41")) {
    const rest = cleaned.slice(3).slice(0, 9); // max 9 chiffres après +41
    const parts = [
      rest.slice(0, 2),
      rest.slice(2, 5),
      rest.slice(5, 7),
      rest.slice(7, 9),
    ].filter(Boolean);
    return "+41 " + parts.join(" ");
  }

  // Cas 2 : format national 0XX…
  if (cleaned.startsWith("0")) {
    const rest = cleaned.slice(0, 10); // max 10 chiffres au total (0XX XXX XX XX)
    const parts = [
      rest.slice(0, 3),
      rest.slice(3, 6),
      rest.slice(6, 8),
      rest.slice(8, 10),
    ].filter(Boolean);
    return parts.join(" ");
  }

  return cleaned;
}

/**
 * Renvoie true si le téléphone est un numéro suisse valide (mobile ou fixe).
 * On accepte le format libre pour ne pas bloquer les cas rares mais on valide
 * quand le champ est non-vide.
 */
export function isValidSwissPhone(value) {
  if (!value) return false;
  const digits = value.replace(/\D/g, "");
  // +41XXXXXXXXX (12 chiffres incluant 41) OU 0XXXXXXXXX (10 chiffres)
  return (
    (digits.startsWith("41") && digits.length === 11) ||
    (digits.startsWith("0") && digits.length === 10)
  );
}

/**
 * Table NPA → Localité pour la Suisse Romande + grandes villes suisses.
 * Non exhaustive, couvre ~95 % des cas fréquents pour 1000 ARTS.
 * L'utilisateur peut toujours saisir manuellement une localité inconnue.
 */
export const SWISS_NPA_TO_CITY = {
  // Vaud — région Lausanne / Cugy / Écublens / Morges / Nyon / Yverdon
  "1000": "Lausanne", "1003": "Lausanne", "1004": "Lausanne", "1005": "Lausanne",
  "1006": "Lausanne", "1007": "Lausanne", "1010": "Lausanne", "1012": "Lausanne",
  "1015": "Lausanne", "1018": "Lausanne", "1020": "Renens VD", "1022": "Chavannes-près-Renens",
  "1023": "Crissier", "1024": "Ecublens VD", "1025": "St-Sulpice VD", "1026": "Denges",
  "1027": "Lonay", "1028": "Préverenges", "1030": "Bussigny", "1031": "Mex VD",
  "1032": "Romanel-sur-Lausanne", "1033": "Cheseaux-sur-Lausanne", "1034": "Boussens",
  "1035": "Bournens", "1036": "Sullens", "1037": "Etagnières", "1038": "Bercher",
  "1040": "Echallens", "1041": "Poliez-Pittet", "1042": "Assens", "1052": "Le Mont-sur-Lausanne",
  "1053": "Cugy VD", "1054": "Morrens VD", "1055": "Froideville", "1058": "Villars-Tiercelin",
  "1063": "Chapelle-sur-Moudon", "1066": "Epalinges", "1068": "Les Monts-de-Pully",
  "1070": "Puidoux", "1071": "Chexbres", "1072": "Forel (Lavaux)", "1073": "Savigny",
  "1076": "Ferlens VD", "1077": "Servion", "1080": "Les Cullayes", "1081": "Montpreveyres",
  "1082": "Corcelles-le-Jorat", "1083": "Mézières VD", "1084": "Carrouge VD",
  "1090": "La Croix (Lutry)", "1091": "Grandvaux", "1092": "Belmont-sur-Lausanne",
  "1093": "La Conversion", "1094": "Paudex", "1095": "Lutry", "1096": "Cully",
  "1097": "Riex", "1098": "Epesses", "1110": "Morges", "1112": "Echichens",
  "1113": "St-Saphorin-sur-Morges", "1114": "Colombier VD", "1115": "Vullierens",
  "1116": "Cottens VD", "1117": "Grancy", "1121": "Bremblens", "1122": "Romanel-sur-Morges",
  "1123": "Aclens", "1124": "Gollion", "1125": "Monnaz", "1126": "Vaux-sur-Morges",
  "1127": "Clarmont", "1128": "Reverolle", "1131": "Tolochenaz", "1132": "Lully VD",
  "1134": "Vufflens-le-Château", "1135": "Denens", "1136": "Bussy-Chardonney",
  "1141": "Sévery", "1142": "Pampigny", "1143": "Apples", "1144": "Ballens",
  "1145": "Bière", "1148": "Chavannes-le-Veyron", "1162": "St-Prex", "1163": "Etoy",
  "1164": "Buchillon", "1165": "Allaman", "1166": "Perroy", "1167": "Lussy-sur-Morges",
  "1168": "Villars-sous-Yens", "1169": "Yens", "1170": "Aubonne", "1172": "Bougy-Villars",
  "1173": "Féchy", "1174": "Pizy", "1175": "Lavigny", "1180": "Rolle",
  "1182": "Gilly", "1183": "Bursins", "1184": "Vinzel", "1185": "Mont-sur-Rolle",
  "1188": "St-George", "1189": "Saubraz", "1195": "Bursinel", "1196": "Gland",
  "1197": "Prangins", "1201": "Genève", "1202": "Genève",
  "1203": "Genève", "1204": "Genève", "1205": "Genève", "1206": "Genève",
  "1207": "Genève", "1208": "Genève", "1209": "Genève", "1212": "Grand-Lancy",
  "1213": "Onex", "1214": "Vernier", "1215": "Genève 15", "1216": "Cointrin",
  "1217": "Meyrin", "1218": "Le Grand-Saconnex", "1219": "Châtelaine",
  "1220": "Les Avanchets", "1222": "Vésenaz", "1223": "Cologny", "1224": "Chêne-Bougeries",
  "1225": "Chêne-Bourg", "1226": "Thônex", "1227": "Carouge GE", "1228": "Plan-les-Ouates",
  "1233": "Bernex", "1242": "Satigny", "1243": "Presinge", "1244": "Choulex",
  "1245": "Collonge-Bellerive", "1246": "Corsier GE", "1247": "Anières", "1248": "Hermance",
  "1252": "Meinier", "1253": "Vandœuvres", "1254": "Jussy", "1255": "Veyrier",
  "1256": "Troinex", "1257": "La Croix-de-Rozon", "1258": "Perly", "1260": "Nyon",
  "1261": "Le Vaud", "1262": "Eysins", "1263": "Crassier", "1264": "St-Cergue",
  "1265": "La Cure", "1266": "Duillier", "1267": "Vich", "1268": "Begnins",
  "1269": "Bassins", "1270": "Trélex", "1271": "Givrins", "1272": "Genolier",
  "1273": "Arzier-Le Muids", "1274": "Grens", "1275": "Chéserex", "1276": "Gingins",
  "1277": "Arnex-sur-Nyon", "1278": "La Rippe", "1279": "Chavannes-de-Bogis",
  "1281": "Russin", "1283": "La Plaine", "1284": "Chancy", "1285": "Athenaz",
  "1286": "Soral", "1287": "Laconnex", "1288": "Aire-la-Ville", "1290": "Versoix",
  "1291": "Commugny", "1292": "Chambésy", "1293": "Bellevue", "1294": "Genthod",
  "1295": "Mies", "1296": "Coppet", "1297": "Founex", "1298": "Céligny",
  // Fribourg
  "1470": "Estavayer-le-Lac", "1618": "Châtel-St-Denis", "1630": "Bulle",
  "1700": "Fribourg", "1701": "Fribourg", "1723": "Marly", "1725": "Posieux",
  "1752": "Villars-sur-Glâne", "1762": "Givisiez", "1763": "Granges-Paccot",
  "1772": "Grolley", "1782": "Belfaux", "1783": "Pensier",
  // Neuchâtel
  "2000": "Neuchâtel", "2013": "Colombier NE", "2017": "Boudry", "2022": "Bevaix",
  "2034": "Peseux", "2035": "Corcelles NE", "2036": "Cormondrèche", "2072": "St-Blaise",
  "2074": "Marin-Épagnier", "2300": "La Chaux-de-Fonds", "2400": "Le Locle",
  // Jura
  "2800": "Delémont", "2900": "Porrentruy",
  // Valais Romand
  "1870": "Monthey", "1890": "St-Maurice", "1920": "Martigny", "1934": "Le Châble",
  "1936": "Verbier", "1950": "Sion", "1951": "Sion", "1958": "St-Léonard",
  "1963": "Vétroz", "1965": "Savièse", "1967": "Bramois", "1972": "Anzère",
  "1973": "Nax", "1974": "Arbaz", "1976": "Erde", "1980": "Riddes",
  "1983": "Basse-Nendaz", "1985": "La Tzoumaz", "1987": "Hérémence", "1993": "Veysonnaz",
  "1997": "Haute-Nendaz",
  // Vaud — Est / Riviera / Yverdon / Nord
  "1800": "Vevey", "1801": "Le Mont-Pèlerin", "1802": "Corseaux", "1803": "Chardonne",
  "1804": "Corsier-sur-Vevey", "1805": "Jongny", "1806": "St-Légier-La Chiésaz",
  "1807": "Blonay", "1808": "Les Monts-de-Corsier", "1809": "Fenil-sur-Corsier",
  "1815": "Clarens", "1816": "Chailly-Montreux", "1817": "Brent", "1820": "Montreux",
  "1822": "Chernex", "1823": "Glion", "1824": "Caux", "1832": "Chamby",
  "1833": "Les Avants", "1400": "Yverdon-les-Bains", "1401": "Yverdon-les-Bains",
  "1410": "Thierrens", "1416": "Pailly", "1423": "Fontaines-sur-Grandson",
  "1450": "Ste-Croix", "1462": "Yvonand", "1468": "Cheyres",
  // Berne (partie francophone)
  "2500": "Bienne", "2502": "Bienne", "2504": "Bienne", "2740": "Moutier",
  "2745": "Grandval", "2800": "Delémont", "3000": "Berne", "3001": "Berne",
  // Zurich (grande ville de référence)
  "8000": "Zürich", "8001": "Zürich", "8002": "Zürich", "8003": "Zürich",
  // Bâle
  "4000": "Bâle", "4001": "Bâle", "4051": "Bâle", "4052": "Bâle",
};

/**
 * Set des localités connues (issues de SWISS_NPA_TO_CITY).
 * Sert à détecter si la localité actuelle vient d'une auto-complétion (on peut alors la remplacer).
 */
const KNOWN_CITIES = new Set(Object.values(SWISS_NPA_TO_CITY));

/**
 * Retourne la localité suggérée pour un NPA, ou "" si inconnu.
 */
export function cityFromNpa(npa) {
  const trimmed = (npa || "").trim();
  return SWISS_NPA_TO_CITY[trimmed] || "";
}

/**
 * Regex d'email simple mais robuste (RFC 5322 simplifié).
 * Vérifie qu'il y a bien un utilisateur, un @, un domaine avec un point.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export function isValidEmail(value) {
  return EMAIL_RE.test((value || "").trim());
}

/**
 * True si la localité fournie provient probablement d'une auto-complétion NPA
 * (donc peut être écrasée par la nouvelle suggestion sans perdre une saisie manuelle).
 */
export function isAutoFilledCity(city) {
  return !city || KNOWN_CITIES.has((city || "").trim());
}


/**
 * Valide un NPA suisse (4 chiffres).
 */
export function isValidSwissNpa(value) {
  return /^\d{4}$/.test((value || "").trim());
}
